import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import moment from 'moment-timezone';
import { Store } from '@ngrx/store';
import { selectSelectedCountry } from 'src/app/store/appContext/app-context.selector';

@Injectable()
export class DateFormatChangesInterceptor implements HttpInterceptor {
  private selectedCountry: any = null;

  constructor(private readonly store: Store) {
    this.store.select(selectSelectedCountry).subscribe((country) => {
      this.selectedCountry = country;
    });
  }
  // Define the endpoints where this should apply
  private readonly targetEndpoints: string[] = [
    '/vehicle/v3/charging',
    'report-service/v3/vehicle-summary-day-wise-report',
    'vehicle/v3/vehicle-summary-report',
    'vehicle/v3/vehicle-monthly-summary-report',
    'vehicle/v3/vehicle-day-wise-summary-report',
    'vehicle/v3/stoppage',
    'report-service/v3/geozone-report',
    'vehicle/v3/overspeed',
    'vehicle/v3/fault-report',
    'report-service/v3/vehicle-data',
    'dashboard/v3/graph-data',
    'vehicle/v3/utilization-report',
    'report-service/v3/cell-imbalance-report',
    '1mdereports/v3/activity-report-summary',
    '1mdereports/v3/vehicle-coordinates',
    '1mdereports/v3/activites-timeline',
    'vehicle/v3/activity-report-download',
    'vehicle/v4/vehicle-map-view',
    'vehicle/v3/trip-report-geozones',
    'vehicle/v1/cancel-trip',
    'vehicle/v3/trip-summary',
    'dashboard/v3/vehicles-calculation',
    'vehicle/v3/legends',
    'dashboard/v3/soc-range',
    'dashboard/v3/alert-count-severity-wise',
    'dashboard/v3/trip-graph',
    'dashboard/v3/chargingduration-energygained',
    'dashboard/v3/distance-energy-efficiency-graph',
    'dashboard/v3/distance-energy-graph',
    'dashboard/v3/utilization-graph',
    'dashboard/v3/trip-stoppage-minutes-split-graph',
    'dashboard/v3/inventory-status',
    'dashboard/v3/vehicle-region-status',
    'dashboard/v3/sla-service-graph',
    'vehicle/v3/vehicles-list',
    'vehicle/v3/service-status-count',
    'vehicle/v3/getVehicleStatus',
    'vehicle/v3/vehicle-info-list',
    'vehicle/v3/vehicle-information',
    'vehicle/v6/vehicle-summary',
    'report-service/v3/trip-report-on-off',
    'report-service/v3/download',
    'report-service/v3/export',
    'vehicle/v3/vehicle-raised-alerts',
    'vehicle/v3/raised-alerts',
    'dashboard/v3/vehicles-graph',
    'dashboard/v3/tco-cumulative',
    'dashboard/v3/tco-utilisation',
    'dashboard/v3/tco-cost',
    'dashboard/v3/tco-range',
    'dashboard/v3/battery-stats-graph',
    'dashboard/v3/soc-cycle-graph',
    'dashboard/v3/soc-current-graph',
    'dashboard/v3/dtc-alert-graph',
    'dashboard/v3/dtc-alerts-severity-stats',
    'dashboard/v3/battery-voltage-moving-charging-graph',
    'dashboard/v3/battery-current-moving-charging-graph',
    'vehicle/v3/service-center-report',
    'vehicle/v3/target-distance-v2',
    'dashboard/v3/soc-range-graph',
    'dashboard/v3/soc-voltage-graph',
    'vehicle/v3/driver-score-graph',
    'vehicle/v3/driver-score-improvements',
    'vehicle/v3/driver-score-vehicle-detail',
    'vehicle/v3/driver-score-breakdown',
    'vehicle/v3/driver-score-vehicle-list',
    'vehicle/v3/driver-rating-summary',
    'vehicle/v3/driver-rating-vehicle-list',
    'vehicle/v3/driver-rating-improvements',
    'vehicle/v3/driver-top-performer',
    'vehicle/v3/driver-score-summary',
    'dashboard/v3/gear-distribution-graph',
    'dashboard/v3/speed-limit',
    'dashboard/v3/speed-range-graph',
    'dashboard/v3/controller-motor-temp',
    'dashboard/v3/motor-power-speed-graph',
    'dashboard/v3/motor-battery',
    'dashboard/v3/motortorque-vehiclespeed',
    'dashboard/v3/distance-energy-hourly-data',
    'dashboard/v3/soc-motor-power-graph',
    'dashboard/v3/average-speed-graph',
    'uam/v1/login',
    'uam/v1/drop-down-data',
    'uam/v1/admin-data',
    'shared-services/v1/vehicles-list-info',
    'shared-services/v1/vehicle',
    'report-service/v3/download-excel',
    'report-service/v3/export-excel',
    'vehicle/v1/location-based-trips',
    'report-service/v3/uptime-report',
    'vehicle/v3/scv-service-record',
    'route-service/v1/requests',
  ];

  private readonly exportDownloadEndpoints: string[] = [
    'report-service/v3/download',
    'report-service/v3/export',
  ];

  private resolveTimezone(): string {
    const stored = this.selectedCountry ?? null;
    if (stored?.timezone && moment.tz.zone(stored.timezone)) {
      return stored.timezone;
    }
    return 'Asia/Kolkata';
  }

  // Convert to UTC and format: yyyy-MM-dd HH:mm:ss
  convertDateFormat(dateValue: string): string {
    if (!dateValue) return dateValue;

    const normalized =
      dateValue.includes(' ') && !dateValue.includes('T')
        ? dateValue.replace(' ', 'T')
        : dateValue;

    const timezone = this.resolveTimezone();
    const utcDate = moment.tz(normalized, timezone).utc();

    if (!utcDate.isValid()) {
      console.warn('Invalid date format:', dateValue);
      return dateValue;
    }
    return utcDate.format('YYYY-MM-DD HH:mm:ss');
  }

  private shouldIntercept(url: string): boolean {
    return this.targetEndpoints.some((endpoint) => url.includes(endpoint));
  }

  private isExportDownloadEndpoint(url: string): boolean {
    return this.exportDownloadEndpoints.some((endpoint) =>
      url.includes(endpoint)
    );
  }

  private convertExportDownloadBodyDates(body: any): any {
    if (!body || typeof body !== 'object' || !body.reportsFilter) {
      return body;
    }

    const filter = body.reportsFilter;

    return {
      ...body,
      reportsFilter: {
        ...filter,
        ...(filter.startDate && {
          startDate: this.convertDateFormat(filter.startDate),
        }),
        ...(filter.endDate && {
          endDate: this.convertDateFormat(filter.endDate),
        }),
      },
    };
  }
  private convertPostBodyDates(body: any): any {
    if (!body || typeof body !== 'object') {
      return body;
    }

    return {
      ...body,
      ...(body.startDate && {
        startDate: this.convertDateFormat(body.startDate),
      }),
      ...(body.endDate && { endDate: this.convertDateFormat(body.endDate) }),
    };
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip if not target endpoint
    if (!this.shouldIntercept(req.url)) {
      return next.handle(req);
    }

    let clonedRequest = req;

    // Export/download: patch dates only inside reportsFilter
    if (this.isExportDownloadEndpoint(req.url) && req.body) {
      clonedRequest = req.clone({
        body: this.convertExportDownloadBodyDates(req.body),
      });
      return next.handle(clonedRequest);
    }
    // Normal POST: patch startDate/endDate directly on body
    if (req.method === 'POST' && req.body) {
      clonedRequest = req.clone({
        body: this.convertPostBodyDates(req.body),
      });
      return next.handle(clonedRequest);
    }

    if (req.params.has('startDate') || req.params.has('endDate')) {
      clonedRequest = req.clone({
        setParams: {
          ...req.params.keys().reduce(
            (acc, key) => {
              if (key === 'startDate') {
                acc['startDate'] = this.convertDateFormat(
                  req.params.get('startDate')!
                );
              } else if (key === 'endDate') {
                acc['endDate'] = this.convertDateFormat(
                  req.params.get('endDate')!
                );
              } else {
                acc[key] = req.params.get(key) ?? '';
              }

              return acc;
            },
            {} as { [key: string]: string }
          ),
        },
      });
    }

    return next.handle(clonedRequest);
  }
}
